import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { toast } from '../shared/ToastContainer';
import { LocationPicker } from './LocationPicker';
import styles from './MapInterface.module.css';

// Initialize Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface MapInterfaceProps {
  onMarkerClick?: (modelId: string) => void;
  onMapClick?: (lngLat: { lng: number; lat: number }) => void;
  selectedModelId?: string;
  initialCenter?: [number, number];
  initialZoom?: number;
}

export const MapInterface: React.FC<MapInterfaceProps> = ({
  onMarkerClick,
  onMapClick,
  selectedModelId: externalSelectedModelId,
  initialCenter = [2.3592241, 48.8634053], // Paris AR placement
  initialZoom = 17, // Zoomed in to Paris
}) => {
  console.log('MapInterface rendered');
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<mapboxgl.Map | null>(null);
  const geolocateControl = useRef<mapboxgl.GeolocateControl | null>(null);
  const markers = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const isInitialized = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(
    externalSelectedModelId
  );
  const maxRetries = 3;
  const retryDelay = 5000;
  const navigate = useNavigate();
  const [arPlacements, setArPlacements] = useState<any[]>([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState<mapboxgl.LngLat | null>(
    null
  );
  const [isNearSelectedInstallation, setIsNearSelectedInstallation] =
    useState(false);
  const [selectedPlacement, setSelectedPlacement] = useState<any>(null);
  const [lastDistance, setLastDistance] = useState<number | null>(null);
  const [isWaitingForLocation, setIsWaitingForLocation] = useState(false);
  const MAX_AR_DISTANCE = 50; // Maximum distance in meters to view AR
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Toggle debug mode based on URL parameter or development environment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');
    setIsDebugMode(
      debugParam === 'true' || import.meta.env.MODE === 'development'
    );
  }, []);

  // For debugging purposes
  useEffect(() => {
    if (isDebugMode) {
      console.log('Current state:', {
        userLocation: userLocation
          ? `${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}`
          : 'not set',
        selectedModelId,
        isNearSelectedInstallation,
        lastDistance:
          lastDistance !== null ? `${lastDistance.toFixed(1)}m` : 'unknown',
        arPlacementsCount: arPlacements.length,
        selectedPlacement: selectedPlacement
          ? `${selectedPlacement.name} at ${selectedPlacement.latitude}, ${selectedPlacement.longitude}`
          : 'none',
      });
    }
  }, [
    userLocation,
    selectedModelId,
    isNearSelectedInstallation,
    lastDistance,
    arPlacements.length,
    selectedPlacement,
    isDebugMode,
  ]);

  useEffect(() => {
    if (!mapContainer.current || isInitialized.current) return;

    console.log('Initializing map interface');
    isInitialized.current = true;

    // Initialize map
    mapInstance.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: initialCenter,
      zoom: initialZoom,
      pitch: 60,
      bearing: 0, // Changed to 0 for better orientation
      antialias: true,
    });

    const map = mapInstance.current;

    // Enable 3D buildings and apply grayscale style
    map.on('style.load', () => {
      console.log('Map style loaded');
      // Commented out grayscale filter due to missing 'background' layer
      // const grayscaleFilter = [
      //   'grayscale',
      //   0.9, // 90% grayscale
      // ];
      // map.setFilter('background', grayscaleFilter);

      // Add 3D building layer
      const layers = map.getStyle().layers;
      const labelLayerId = layers.find(
        layer =>
          layer.type === 'symbol' && layer.layout && layer.layout['text-field']
      )?.id;

      map.addLayer(
        {
          id: '3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 15,
          paint: {
            'fill-extrusion-color': '#aaa',
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'height'],
            ],
            'fill-extrusion-base': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'min_height'],
            ],
            'fill-extrusion-opacity': 0.6,
          },
        },
        labelLayerId
      );
    });

    // Add navigation controls to bottom-right
    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

    // Initialize geolocation control
    geolocateControl.current = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
      showAccuracyCircle: false,
    });

    // Listen for geolocate events to capture user position
    geolocateControl.current.on('geolocate', (e: any) => {
      console.log('Geolocate event triggered', e);
      const userLng = e.coords.longitude;
      const userLat = e.coords.latitude;
      console.log(`User location: ${userLat}, ${userLng}`);
      setUserLocation(new mapboxgl.LngLat(userLng, userLat));
      setIsWaitingForLocation(false);

      // After getting user location, fit map to show both user and AR placements
      setTimeout(() => {
        if (arPlacements.length > 0) {
          console.log(
            'Calling fitMapToShowUserAndPlacements from geolocate event'
          );
          fitMapToShowUserAndPlacements(userLng, userLat);
        } else {
          console.log('No AR placements available to fit map to');
          // Load placements if they haven't been loaded yet
          loadNearbyModels();
        }

        // If we have a selected model, check if we're near it
        if (selectedModelId) {
          checkProximityToSelectedInstallation(userLng, userLat);
        }
      }, 500);
    });

    // Handle geolocation errors
    geolocateControl.current.on('error', (e: any) => {
      console.error('Geolocation error:', e);
      setIsWaitingForLocation(false);
      toast.error(
        'Unable to get your location. Please check your device settings and try again.'
      );
    });

    // Trigger geolocation on map load
    map.on('load', () => {
      console.log('Mapbox map loaded event fired');
      setIsLoading(false);
      setIsMapLoaded(true);
    });

    map.on('click', handleMapClick);

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      console.log('Cleaning up map interface');
      if (mapInstance.current) {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        markers.current.forEach(marker => marker.remove());
        markers.current.clear();
        mapInstance.current.remove();
        mapInstance.current = null;
        geolocateControl.current = null;
        isInitialized.current = false;
      }
    };
  }, []);

  // Calculate distance between two points in meters using the Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Check if user is near the selected installation
  const checkProximityToSelectedInstallation = (
    userLng: number,
    userLat: number
  ) => {
    if (!selectedModelId) {
      console.log('No selected model ID, cannot check proximity');
      setIsNearSelectedInstallation(false);
      setLastDistance(null);
      setSelectedPlacement(null);
      return;
    }

    if (!userLocation) {
      console.log('No user location, cannot check proximity');
      setIsNearSelectedInstallation(false);
      setLastDistance(null);
      return;
    }

    const placement = arPlacements.find(p => p.id === selectedModelId);
    if (!placement) {
      console.log('Selected placement not found in AR placements');
      setIsNearSelectedInstallation(false);
      setLastDistance(null);
      setSelectedPlacement(null);
      return;
    }

    // Store the selected placement for reference
    setSelectedPlacement(placement);

    const placementLat = placement.latitude || placement.location?.latitude;
    const placementLng = placement.longitude || placement.location?.longitude;

    if (!placementLat || !placementLng) {
      console.log('Placement missing latitude or longitude');
      setIsNearSelectedInstallation(false);
      setLastDistance(null);
      return;
    }

    console.log(
      `Calculating distance between user (${userLat}, ${userLng}) and placement (${placementLat}, ${placementLng})`
    );
    const distance = calculateDistance(
      userLat,
      userLng,
      placementLat,
      placementLng
    );
    setLastDistance(distance);

    const isNear = distance <= MAX_AR_DISTANCE;
    setIsNearSelectedInstallation(isNear);

    if (isNear) {
      console.log(`User is near installation (${Math.round(distance)}m)`);
      toast.success(
        `You're near the ${placement.name}! You can now view it in AR.`
      );
    } else {
      console.log(
        `User is ${Math.round(
          distance
        )}m away from installation (max allowed: ${MAX_AR_DISTANCE}m)`
      );
    }
  };

  // Update proximity check when user location or selected model changes
  useEffect(() => {
    if (userLocation && selectedModelId) {
      console.log(
        'Checking proximity due to userLocation or selectedModelId change'
      );
      checkProximityToSelectedInstallation(userLocation.lng, userLocation.lat);
    }
  }, [userLocation, selectedModelId, arPlacements]);

  const fitMapToShowUserAndPlacements = (userLng: number, userLat: number) => {
    const map = mapInstance.current;
    if (!map) {
      console.error('Map instance not available');
      return;
    }

    if (arPlacements.length === 0) {
      console.error('No AR placements available');
      return;
    }

    console.log(
      `Fitting map to show user (${userLat}, ${userLng}) and ${arPlacements.length} placements`
    );

    try {
      // Create a bounds object that includes the user's location
      const bounds = new mapboxgl.LngLatBounds();

      // Add user location to bounds
      bounds.extend([userLng, userLat]);
      console.log(`Added user location to bounds: ${userLat}, ${userLng}`);

      // Add all AR placement locations to bounds
      let placementsAdded = 0;
      arPlacements.forEach(placement => {
        const lat = placement.latitude || placement.location?.latitude;
        const lng = placement.longitude || placement.location?.longitude;
        if (lat && lng) {
          bounds.extend([lng, lat]);
          console.log(`Added placement to bounds: ${lat}, ${lng}`);
          placementsAdded++;
        } else {
          console.warn('Placement missing lat/lng:', placement);
        }
      });

      // If we have valid bounds with at least one placement, fit the map to them
      if (!bounds.isEmpty() && placementsAdded > 0) {
        console.log(`Fitting map to bounds with ${placementsAdded} placements`);
        map.fitBounds(bounds, {
          padding: 100, // Add some padding around the bounds
          maxZoom: 18, // Don't zoom in too far
          duration: 1500, // Smooth animation
        });

        toast.info('Showing your location and nearby AR content');
      } else {
        console.error('No valid bounds to fit map to');
      }
    } catch (error) {
      console.error('Error fitting map to bounds:', error);
    }
  };

  const handleOnline = () => {
    setIsOffline(false);
    void loadNearbyModels();
    toast.success('Connection restored. Updating map...');
  };

  const handleOffline = () => {
    setIsOffline(true);
    toast.warning('You are offline. Some features may be limited.');
  };

  const updateMarkers = (arPlacements: any[]) => {
    const map = mapInstance.current;
    if (!map) return;

    // Remove existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current.clear();

    // Add new markers for each placement
    arPlacements.forEach(placement => {
      const lat = placement.latitude || placement.location?.latitude;
      const lng = placement.longitude || placement.location?.longitude;
      if (!lat || !lng) return;

      const markerElement = document.createElement('div');
      markerElement.className =
        placement.name === 'Susanna Boots'
          ? styles.susannaMarker
          : styles.marker;

      const marker = new mapboxgl.Marker({
        element: markerElement,
        anchor: 'bottom',
        offset: [0, -20],
      })
        .setLngLat([lng, lat])
        .setPopup(
          new mapboxgl.Popup({
            offset: 25,
            closeButton: false,
            maxWidth: '300px',
          }).setHTML(`
            <div class="${styles.popup}">
              <h3>${placement.name}</h3>
              <p>${placement.description || 'No description'}</p>
            </div>
          `)
        )
        .addTo(map);

      marker.getElement().addEventListener('click', () => {
        map.flyTo({
          center: [lng, lat],
          zoom: 19,
          pitch: 60,
          bearing: 0,
          duration: 2000,
          essential: true,
        });

        marker.togglePopup();
        setSelectedModelId(placement.id);
        onMarkerClick?.(placement.id);

        // Check if user is near this installation
        if (userLocation) {
          checkProximityToSelectedInstallation(
            userLocation.lng,
            userLocation.lat
          );
        } else {
          // If no user location, prompt to get it
          toast.info(
            'Please enable your location to check if you can view this in AR'
          );
        }
      });

      markers.current.set(placement.id, marker);
    });

    // If we already have user location, fit map to show both
    if (userLocation) {
      console.log(
        'User location available, fitting map to show user and placements'
      );
      fitMapToShowUserAndPlacements(userLocation.lng, userLocation.lat);
    }
  };

  const loadNearbyModels = async () => {
    const map = mapInstance.current;
    if (!map) return;

    try {
      console.log('Fetching AR placements...');
      const placements = await fetchARPlacements();
      console.log('Fetched AR placements:', placements);
      setArPlacements(placements);

      // If we already have the user's location, fit the map to show both
      if (userLocation && placements.length > 0) {
        console.log('Fitting map after loading placements with user location');
        fitMapToShowUserAndPlacements(userLocation.lng, userLocation.lat);

        // If we already have a selected model, check proximity again with new placements
        if (selectedModelId) {
          checkProximityToSelectedInstallation(
            userLocation.lng,
            userLocation.lat
          );
        }
      }
      // If no user location but we have placements, fit the map to show all placements
      else if (placements.length > 0) {
        console.log('No user location, fitting map to show all placements');
        fitMapToShowAllPlacements();
      }
    } catch (error) {
      console.error('Failed to load AR placements:', error);
      toast.error('Failed to load AR placements');
    }
  };

  // Update selected marker
  useEffect(() => {
    console.log('selectedModelId changed to:', selectedModelId);
    markers.current.forEach((marker, id) => {
      const element = marker.getElement();
      if (id === selectedModelId) {
        element.classList.add(styles.selectedMarker);
      } else {
        element.classList.remove(styles.selectedMarker);
      }
    });

    // When a new model is selected, check if we're near it
    if (selectedModelId && userLocation) {
      checkProximityToSelectedInstallation(userLocation.lng, userLocation.lat);
    }
  }, [selectedModelId, userLocation]);

  // Update markers when external selectedModelId changes
  useEffect(() => {
    if (externalSelectedModelId !== selectedModelId) {
      setSelectedModelId(externalSelectedModelId);
    }
  }, [externalSelectedModelId]);

  const calculateRadius = (bounds: mapboxgl.LngLatBounds): number => {
    const center = bounds.getCenter();
    const ne = bounds.getNorthEast();

    // Calculate distance from center to northeast corner in meters
    return calculateDistance(center.lat, center.lng, ne.lat, ne.lng);
  };

  const handleLocationSelect = (location: { lng: number; lat: number }) => {
    const map = mapInstance.current;
    if (!map) return;

    // Store user location when selected
    setUserLocation(new mapboxgl.LngLat(location.lng, location.lat));

    // If we have AR placements, fit map to show both user and placements
    if (arPlacements.length > 0) {
      console.log('Fitting map from handleLocationSelect with AR placements');
      fitMapToShowUserAndPlacements(location.lng, location.lat);
    } else {
      // If no placements, just fly to user location
      console.log('No AR placements available, just flying to user location');
      map.flyTo({
        center: [location.lng, location.lat],
        zoom: 15,
        duration: 1500,
      });

      // Try to load placements if they haven't been loaded yet
      loadNearbyModels();
    }

    // Check proximity to selected installation
    if (selectedModelId) {
      checkProximityToSelectedInstallation(location.lng, location.lat);
    }

    onMapClick?.(location);
  };

  const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
    onMapClick?.({
      lng: e.lngLat.lng,
      lat: e.lngLat.lat,
    });
  };

  // Update map click handler in useEffect
  useEffect(() => {
    if (!mapInstance.current) return;

    mapInstance.current.on('click', handleMapClick);

    return () => {
      mapInstance.current?.off('click', handleMapClick);
    };
  }, []);

  // Make sure AR placements are loaded and markers are updated
  useEffect(() => {
    if (isMapLoaded && arPlacements.length > 0) {
      console.log('Updating markers with AR placements');
      updateMarkers(arPlacements);

      // If no user location is available, fit the map to show all AR placements
      if (!userLocation) {
        fitMapToShowAllPlacements();
      }
    }
  }, [isMapLoaded, arPlacements]);

  // Load AR placements when map is ready
  useEffect(() => {
    if (isMapLoaded) {
      console.log('Map loaded, loading AR placements');
      loadNearbyModels();
    }
  }, [isMapLoaded]);

  // Make sure to update the map view when both user location and AR placements are available
  useEffect(() => {
    if (userLocation && arPlacements.length > 0 && isMapLoaded) {
      console.log(
        'Both user location and AR placements available, updating map view'
      );
      fitMapToShowUserAndPlacements(userLocation.lng, userLocation.lat);
    }
  }, [userLocation, arPlacements.length, isMapLoaded]);

  // Function to fit map to show all AR placements
  const fitMapToShowAllPlacements = () => {
    const map = mapInstance.current;
    if (!map || arPlacements.length === 0) return;

    console.log(`Fitting map to show all ${arPlacements.length} AR placements`);

    try {
      // Create a bounds object that includes all placements
      const bounds = new mapboxgl.LngLatBounds();

      // Add all AR placement locations to bounds
      let placementsAdded = 0;
      arPlacements.forEach(placement => {
        const lat = placement.latitude || placement.location?.latitude;
        const lng = placement.longitude || placement.location?.longitude;
        if (lat && lng) {
          bounds.extend([lng, lat]);
          placementsAdded++;
        }
      });

      // If we have valid bounds with at least one placement, fit the map to them
      if (!bounds.isEmpty() && placementsAdded > 0) {
        map.fitBounds(bounds, {
          padding: 100, // Add some padding around the bounds
          maxZoom: 16, // Don't zoom in too far
          duration: 1500, // Smooth animation
        });

        toast.info(`Showing ${placementsAdded} AR installations`);
      }
    } catch (error) {
      console.error('Error fitting map to bounds:', error);
    }
  };

  const getBackendBaseUrl = () => {
    if (import.meta.env.MODE === 'production') {
      return import.meta.env.VITE_APP_BACKEND_BASEURL_LIVE;
    }
    return import.meta.env.VITE_APP_backend_baseUrl_localTest;
  };

  async function fetchARPlacements() {
    const baseUrl = getBackendBaseUrl();
    console.log('Fetching AR placements from:', baseUrl);

    const res = await fetch(
      `${baseUrl}/ar-placements?_limit=100&_sort=created_at:desc`
    );

    if (!res.ok) {
      console.error(
        `Failed to fetch AR placements: ${res.status} ${res.statusText}`
      );
      throw new Error('Failed to fetch AR placements');
    }

    const data = await res.json();
    console.log(
      `Fetched ${Array.isArray(data) ? data.length : 0} AR placements`
    );
    return Array.isArray(data) ? data : [];
  }

  // Function to request user location
  const requestUserLocation = () => {
    if (geolocateControl.current) {
      console.log('Requesting user location via geolocate control');
      geolocateControl.current.trigger();
      setIsWaitingForLocation(true);
      toast.info(
        'Requesting your location... Please allow access in the prompt.'
      );
    } else {
      console.error('Geolocation control not available');
      toast.error(
        'Location services not available. Please check your browser settings.'
      );
    }
  };

  // Handle AR button click
  const handleArButtonClick = () => {
    if (!selectedModelId) {
      toast.info('Please select an AR installation on the map first.');
      return;
    }

    if (!userLocation) {
      console.log('No user location, requesting location for selected model');
      requestUserLocation();
      return;
    }

    // At this point, selectedModelId AND userLocation exist.
    if (isNearSelectedInstallation) {
      console.log('User is near installation, navigating to AR view');
      navigate('/ar');
    } else {
      // Not near, selectedModelId and userLocation exist
      console.log('User is NOT near installation, showing message');
      const placementName = selectedPlacement?.name || 'the installation';
      const distanceMessage = lastDistance !== null ? `You are currently ${Math.round(lastDistance)}m away.` : "Distance unknown.";
      toast.info(
        `You need to be within ${MAX_AR_DISTANCE}m of ${placementName} to view it in AR. ${distanceMessage}`
      );

      // If we have the selected installation coordinates, fly to it
      if (selectedPlacement) {
        const lat =
          selectedPlacement.latitude || selectedPlacement.location?.latitude;
        const lng =
          selectedPlacement.longitude || selectedPlacement.location?.longitude;
        if (lat && lng && mapInstance.current) {
          console.log(`Flying to installation at ${lat}, ${lng}`);
          mapInstance.current.flyTo({
            center: [lng, lat],
            zoom: 18,
            duration: 1500,
          });
        }
      }
    }
  };

  // Debug button to force proximity check
  const debugForceNear = () => {
    console.log('DEBUG: Forcing proximity to selected installation');
    setIsNearSelectedInstallation(true);
    toast.success('DEBUG: Proximity override enabled');
  };

  return (
    <div className={styles.container}>
      <div ref={mapContainer} className={styles.map}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <LoadingSpinner size="large" theme="light" />
          </div>
        )}
        {!isLoading && arPlacements.length === 0 && (
          <div className={styles.loadingOverlay}>
            <span>No AR placements found.</span>
          </div>
        )}
      </div>
      <LocationPicker
        onLocationSelect={handleLocationSelect}
        geolocateControl={geolocateControl.current}
        map={mapInstance.current}
      />

      {/* Show location request button if no user location is available */}
      {!userLocation && !isWaitingForLocation && isDebugMode && (
        <div className={styles.locationButtonContainer}>
          <div className={styles.locationNote}>
            {arPlacements.length > 0
              ? `Showing ${arPlacements.length} AR installations on the map`
              : 'Loading AR installations...'}
          </div>
        </div>
      )}

      {isWaitingForLocation && (
        <div className={styles.locationButton}>
          <LoadingSpinner size="small" theme="dark" /> Getting your location...
        </div>
      )}

      {/* Debug information overlay (only visible in debug mode) */}
      {isDebugMode && (
        <div className={styles.debugInfo}>
          <p>
            User location:{' '}
            {userLocation
              ? `${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}`
              : 'Not set'}
          </p>
          <p>Selected ID: {selectedModelId || 'None'}</p>
          <p>Selected: {selectedPlacement?.name || 'None'}</p>
          <p>Is near: {isNearSelectedInstallation ? 'Yes' : 'No'}</p>
          <p>
            Distance:{' '}
            {lastDistance !== null ? `${lastDistance.toFixed(1)}m` : 'Unknown'}
          </p>
          <button onClick={debugForceNear}>Force Near</button>
        </div>
      )}

      {/* AR button - always visible, behavior and appearance change based on state */}
        <button
          className={`${styles.arButton} ${
          (!selectedModelId || (selectedModelId && !!userLocation && !isNearSelectedInstallation))
              ? styles.arButtonDisabled
              : ''
        }`.trim()}
          onClick={handleArButtonClick}
        disabled={!selectedModelId} // Functionally disable if no model is selected
        >
        {
          !selectedModelId
            ? 'Select the installation nearby to view in AR'
            : !userLocation
            ? '📍 Enable location to see in AR'
            : isNearSelectedInstallation
            ? '👀 View in AR'
            : `🚶 Visit to see in AR ${lastDistance !== null ? `(${Math.round(lastDistance)}m)` : ''}`
        }
        </button>

      {isOffline && (
        <div className={styles.offlineIndicator}>
          <span className={styles.offlineIcon}>📡</span>
          Offline Mode
        </div>
      )}
    </div>
  );
};

export default MapInterface;
